/* oxlint-disable no-console */

import { isServer } from '../env-helper';
import { FetchError } from './error';
import { CreateOptions, FetchOptions } from './types';

/**
 * x-prex-authorization = CF_AUTH
 * vercel 에서 build 시에는 ip whitelist 를 통과하지 못한다 (serverless 라서 ip가 유동적)
 * stg, sandbox 환경에서 빌드하기 위해서는 api, static path 에 접근하기 위한 header 값을 추가
 * prod (flipster) 환경에서는 임시로 wordpress 에 접근하기 위해서 필요
 * contentful 로 넘어가면 prod 환경은 필요 없어짐
 */
export class Fetcher {
  constructor(private readonly options: CreateOptions) {}

  async fetch<R extends unknown = unknown>(url: string, { params, body, ...options }: FetchOptions = {}): Promise<R> {
    const controller = new AbortController();

    const reqOptions: RequestInit = options;
    reqOptions.headers = {
      ...this.options.headers,
      ...options.headers,
    };
    reqOptions.signal = controller.signal;
    reqOptions.signal.addEventListener('abort', () => {
      console.log('Request aborted');
    });

    const input = new URL((this.options.baseURL || '') + url);
    if (params) input.search = this.buildQueryString(params);
    if (body) reqOptions.body = JSON.stringify(body);

    const req = new Request(input, reqOptions);
    const timeout = setTimeout(
      () => {
        controller.abort();
      },
      this.options.timeout ?? 10 * 1000,
    );

    try {
      const res = await fetch(req);

      if (!res.ok) {
        if (isServer) console.log(req.url);
        const message = await res.text();
        const errorCode = res.status;
        throw new FetchError(req, res, `[${errorCode}] ${message}`);
      }

      // TODO 이걸 좀더 범용적으로 깔끔하게 처리할 방식 변경 필요 임시...
      if (Fetcher.matchContentType('json', req, res)) return (await res.json()) as R;
      if (Fetcher.matchContentType('text', req, res)) return (await res.text()) as R;
      return res as R;
    } finally {
      clearTimeout(timeout);
    }
  }

  private buildQueryString(params: Record<string, string | string[]>): string {
    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (Array.isArray(value)) {
        // 배열인 경우 각 값을 key[]= 형식으로 추가
        value.forEach((item) => {
          searchParams.append(`${key}[]`, item);
        });
      } else {
        // 문자열인 경우 일반적인 형식으로 추가
        searchParams.append(key, value);
      }
    }

    return searchParams.toString();
  }

  static matchContentType(type: 'json' | 'text', req: Request, res: Response) {
    return req.headers?.get('Content-Type')?.includes(type) || res.headers?.get('Content-Type')?.includes(type);
  }
}
